import { DropdownSelect } from '@components/common';
import { FormInput } from '@components/common/FormField';
import { Trash2 } from 'lucide-react';
import React from 'react';
import { useStockTransferFormContext } from './StockTransferFormContext';
import {
  getLineAvailability,
  getRowItemOptions,
  LineForm,
} from './stockTransferFormUtils';

interface Props {
  line: LineForm;
}

export const StockTransferLineItemRow: React.FC<Props> = ({ line }) => {
  const {
    form,
    units,
    itemOptions,
    balanceByItemId,
    isBalancesFetching,
    setLine,
    removeLine,
    uid,
  } = useStockTransferFormContext();

  const selectedItem = itemOptions.items.find(
    (i) => i.id === line.inventoryItemId,
  );
  const unit = selectedItem
    ? units.find((u) => u.id === selectedItem.stock_unit_id)
    : undefined;
  const unitLabel = unit?.abbreviation || unit?.name || '';

  const rowOptions = getRowItemOptions(
    itemOptions.options,
    form.lines,
    line.key,
  );

  const { hasBalanceData, availableQty, isOverAvailable, hasQuantityError } =
    getLineAvailability(line, balanceByItemId, form.fromLocationId);

  return (
    <tr className="border-t border-gray-100">
      <td className="px-3 py-2 align-top">
        <DropdownSelect
          label=""
          value={line.inventoryItemId}
          options={rowOptions}
          placeholder="Select item"
          searchable
          searchPlaceholder="Search items"
          onSearchChange={itemOptions.setSearchQuery}
          hasMoreOptions={itemOptions.hasMore}
          isLoadingOptions={itemOptions.isLoading}
          onLoadMoreOptions={itemOptions.loadMore}
          emptyStateLabel="No items found."
          customDisplayLabel={itemOptions.getItemLabel(line.inventoryItemId)}
          onChange={(value) => setLine(line.key, 'inventoryItemId', value)}
          menuPlacement="up"
        />
      </td>
      <td className="px-3 py-2 align-top">
        <p className="mt-2 text-sm text-gray-500 capitalize">
          {unitLabel || '—'}
        </p>
      </td>
      <td className="px-3 py-2 align-top">
        {!line.inventoryItemId ? (
          <p className="mt-2 text-sm text-gray-400">—</p>
        ) : !form.fromLocationId ? (
          <p className="mt-2 text-sm text-gray-400">—</p>
        ) : isBalancesFetching && !hasBalanceData ? (
          <p className="mt-2 text-sm text-gray-400">Checking stock…</p>
        ) : (
          <p
            className={`mt-2 text-sm ${
              isOverAvailable ? 'font-medium text-red-600' : 'text-gray-600'
            }`}
          >
            {availableQty}
          </p>
        )}
      </td>
      <td className="px-3 py-2 align-top">
        <div className="flex items-start gap-2">
          <div className="w-32 flex flex-col gap-1">
            <FormInput
              id={`${uid}-qty-${line.key}`}
              label=""
              type="number"
              min="0.0001"
              step="0.0001"
              value={line.quantity}
              onChange={(e) => setLine(line.key, 'quantity', e.target.value)}
              placeholder="Qty"
            />
            {line.inventoryItemId && hasQuantityError ? (
              <p className="text-xs font-medium text-red-600">
                Quantity must be greater than 0
              </p>
            ) : line.inventoryItemId && isOverAvailable ? (
              <p className="text-xs font-medium text-red-600">
                Exceeds available stock ({availableQty} available)
              </p>
            ) : null}
          </div>
          {form.lines.length > 1 && (
            <button
              type="button"
              onClick={() => removeLine(line.key)}
              className="mt-1 rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-red-500"
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};
